import { useCallback, useEffect, useRef, useState } from 'react'
import { motion, useMotionValueEvent, useScroll } from 'motion/react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import 'moment/locale/en-gb'
import 'moment/locale/id'
import 'moment/locale/ko'

import { cn, getLocales, setLocales } from '@/utils'
import { useClickOutside } from '@/hooks'

export const Header = () => {
	const { scrollY } = useScroll()
	const { t, i18n } = useTranslation()

	const refDesktop = useRef<HTMLDivElement>(null)
	const refMobile = useRef<HTMLDivElement>(null)

	const [isScrolled, setIsScrolled] = useState<boolean>(false)
	const [activeSection, setActiveSection] = useState<string>('home')
	const [isNearBottom, setIsNearBottom] = useState<boolean>(false)
	const [showMenu, setShowMenu] = useState<boolean>(false)
	const [showLanguage, setShowLanguage] = useState<boolean>(false)
	const [currentLanguage, setCurrentLanguage] = useState<string>('id')

	useClickOutside([refDesktop, refMobile], () => setShowLanguage(false))

	const getCurrentLanguage = useCallback(async () => {
		const lang = await getLocales()
		if (lang) {
			setCurrentLanguage(lang)
		}
	}, [])

	const changeLanguage = (lang: string) => {
		setCurrentLanguage(lang)
		i18n.changeLanguage(lang)
		setLocales(lang)
		setShowLanguage(false)
	}

	useEffect(() => {
		getCurrentLanguage()
	}, [getCurrentLanguage])

	useEffect(() => {
		switch (i18n.language) {
			case 'en':
				moment.locale('en-gb')
				break
			case 'kr':
				moment.locale('ko')
				break
			case 'id':
			default:
				moment.locale('id')
				break
		}
	}, [i18n.language])

	useMotionValueEvent(scrollY, 'change', latest => {
		setIsScrolled(latest > 50)
	})

	useEffect(() => {
		const sections = document.querySelectorAll('section[id]')

		const observer = new IntersectionObserver(
			entries => {
				if (!isNearBottom) {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							setActiveSection(entry.target.id)
						}
					})
				}
			},
			{
				root: null,
				rootMargin: '0px',
				threshold: 0.5
			}
		)

		sections.forEach(section => observer.observe(section))

		const handleScrollBottom = () => {
			const { scrollY, innerHeight } = window
			const scrollHeight = document.documentElement.scrollHeight
			const distanceFromBottom = scrollHeight - (scrollY + innerHeight)

			if (distanceFromBottom <= 50) {
				setIsNearBottom(true)
				setActiveSection('contact')
				return
			}

			if (distanceFromBottom <= 400) {
				setIsNearBottom(true)
				setActiveSection('product')
				return
			}

			if (isNearBottom && distanceFromBottom > 400) {
				setIsNearBottom(false)
			}
		}

		window.addEventListener('scroll', handleScrollBottom)

		return () => {
			observer.disconnect()
			window.removeEventListener('scroll', handleScrollBottom)
		}
	}, [isNearBottom])

	const handleScroll = (id: string) => {
		const section = document.getElementById(id)
		if (section) {
			section.scrollIntoView({ behavior: 'smooth' })
		}
	}

	return (
		<motion.header
			className="fixed top-0 w-full flex justify-between items-center z-[100]"
			initial={{ y: -100 }}
			animate={{
				y: 0,
				backgroundColor: isScrolled
					? 'rgba(255,255,255,1)'
					: 'rgba(255,255,255,0)'
			}}
			transition={{
				duration: 0.3,
				y: {
					duration: 1
				}
			}}
		>
			<motion.img
				className="sm:block hidden h-20 origin-top-left"
				animate={{
					height: isScrolled ? 80 : 128
				}}
				transition={{
					duration: 0.3
				}}
				src={require('@/assets/images/arirang-corner.png')}
				alt="arirang"
			/>
			<motion.img
				className="sm:hidden block h-16 origin-top-left"
				animate={{
					height: isScrolled ? 64 : 90
				}}
				transition={{
					duration: 0.3
				}}
				src={require('@/assets/images/arirang-corner.png')}
				alt="arirang"
			/>
			<motion.div
				className="lg:hidden h-full flex justify-center items-center aspect-square"
				animate={{
					height: isScrolled ? 64 : 90
				}}
				transition={{
					duration: 0.3
				}}
				onClick={() => setShowMenu(true)}
			>
				<img
					className="h-10 transition-all duration-300 cursor-pointer"
					src={require('@/assets/images/menu.png')}
					alt="arirang"
				/>
			</motion.div>
			<motion.div
				className="lg:hidden fixed top-0 right-0 w-full h-full bg-white p-6 overflow-hidden"
				initial={{ x: '100%' }}
				animate={{ x: showMenu ? 0 : '100%' }}
				transition={{
					duration: 0.3
				}}
			>
				<div className="flex justify-between items-center">
					<motion.div
						className="cursor-pointer"
						initial={{ x: 10, opacity: 1 }}
						animate={{ x: showMenu ? 0 : 10, opacity: showMenu ? 1 : 0 }}
						transition={{
							duration: showMenu ? 0.5 : 0.5,
							delay: showMenu ? 0.3 : 0
						}}
						onClick={() => setShowMenu(false)}
					>
						<img
							className="inline-block h-8"
							src={require('@/assets/icons/right-arrow.png')}
							alt="arirang"
						/>
					</motion.div>
					<div ref={refMobile} className="relative">
						<div
							className="relative bg-white border border-gray-100 p-2 rounded-full flex items-center gap-1 cursor-pointer"
							onClick={() => setShowLanguage(!showLanguage)}
						>
							<img
								className="h-6 shrink-0"
								src={
									currentLanguage === 'en'
										? require(`@/assets/icons/en.png`)
										: currentLanguage === 'kr'
											? require(`@/assets/icons/kr.png`)
											: require(`@/assets/icons/id.png`)
								}
								alt="arirang"
							/>
							<span className="font-semibold text-sm text-black">
								{currentLanguage?.toUpperCase()}
							</span>
							<img
								className="h-2 shrink-0"
								src={require('@/assets/icons/chevron.png')}
								alt="arirang"
							/>
						</div>
						<div
							className={cn(
								'absolute top-full right-0 invisible opacity-0 transition-all ease-in duration-300 translate-y-2',
								showLanguage && 'visible opacity-100 translate-y-0'
							)}
						>
							<div className="w-32 py-1 rounded-xl overflow-hidden bg-white border border-gray-100 mt-1">
								<div
									className="bg-white hover:bg-gray-50 flex-1 flex items-center gap-2 py-2 px-2 border-b border-b-gray-50 cursor-pointer"
									onClick={() => changeLanguage('id')}
								>
									<img
										className="h-6 shrink-0"
										src={require('@/assets/icons/id.png')}
										alt="arirang"
									/>
									<span className="font-semibold text-sm text-black">
										Indonesia
									</span>
								</div>
								<div
									className="bg-white hover:bg-gray-50 flex-1 flex items-center gap-2 py-2 px-2 border-b border-b-gray-50 cursor-pointer"
									onClick={() => changeLanguage('en')}
								>
									<img
										className="h-6 shrink-0"
										src={require('@/assets/icons/en.png')}
										alt="arirang"
									/>
									<span className="font-semibold text-sm text-black">
										English
									</span>
								</div>
								<div
									className="bg-white hover:bg-gray-50 flex-1 flex items-center gap-2 py-2 px-2 cursor-pointer"
									onClick={() => changeLanguage('kr')}
								>
									<img
										className="h-6 shrink-0"
										src={require('@/assets/icons/kr.png')}
										alt="arirang"
									/>
									<span className="font-semibold text-sm text-black">
										Korea
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
				<nav className="flex flex-col gap-4 mt-5">
					<div className="font-bold text-xl text-black">{t('menu')}</div>
					<span
						className={cn(
							'font-normal hover:font-bold text-base text-black cursor-pointer',
							activeSection === 'home' && 'font-bold text-blue-600'
						)}
						onClick={() => {
							setShowMenu(false)
							setTimeout(() => handleScroll('home'), 300)
						}}
					>
						{t('home')}
					</span>
					<span
						className={cn(
							'font-normal hover:font-bold text-base text-black cursor-pointer',
							activeSection === 'program' && 'font-bold text-blue-600'
						)}
						onClick={() => {
							setShowMenu(false)
							setTimeout(() => handleScroll('program'), 300)
						}}
					>
						{t('program')}
					</span>
					<span
						className={cn(
							'font-normal hover:font-bold text-base text-black cursor-pointer',
							activeSection === 'product' && 'font-bold text-blue-600'
						)}
						onClick={() => {
							setShowMenu(false)
							setTimeout(() => handleScroll('product'), 300)
						}}
					>
						{t('product')}
					</span>
					<span
						className={cn(
							'font-normal hover:font-bold text-base text-black cursor-pointer',
							activeSection === 'contact' && 'font-bold text-blue-600'
						)}
						onClick={() => {
							setShowMenu(false)
							setTimeout(() => handleScroll('contact'), 300)
						}}
					>
						{t('contact_us')}
					</span>
					<a
						href="https://shake.arirang.co.id/member"
						className="w-full flex justify-center items-center sm:h-12 h-10 px-2 border border-blue-600 rounded-full transition-all duration-300 hover:font-bold"
					>
						<span className="xl:text-lg lg:text-sm md:text-base text-sm text-blue-600">
							{t('login_now')}
						</span>
					</a>
					<a
						href="https://shake.arirang.co.id/member/register"
						className="w-full flex justify-center items-center sm:h-12 h-10 px-2 border bg-blue-600 border-blue-600 rounded-full transition-all duration-300 hover:font-bold"
					>
						<span className="xl:text-lg lg:text-sm md:text-base text-sm text-white line-clamp-1 overflow-hidden">
							{t('register_and_collect_point')}
						</span>
					</a>
				</nav>
			</motion.div>
			<nav className="lg:flex hidden items-center gap-10 xl:mr-20 mr-10">
				<span
					className={cn(
						'font-normal text-base text-black cursor-pointer',
						activeSection === 'home' && 'font-bold text-blue-600'
					)}
					onClick={() => handleScroll('home')}
				>
					{t('home')}
				</span>
				<span
					className={cn(
						'font-normal text-base text-black cursor-pointer',
						activeSection === 'program' && 'font-bold text-blue-600'
					)}
					onClick={() => handleScroll('program')}
				>
					{t('program')}
				</span>
				<span
					className={cn(
						'font-normal text-base text-black cursor-pointer',
						activeSection === 'product' && 'font-bold text-blue-600'
					)}
					onClick={() => handleScroll('product')}
				>
					{t('product')}
				</span>
				<span
					className={cn(
						'font-normal text-base text-black cursor-pointer',
						activeSection === 'contact' && 'font-bold text-blue-600'
					)}
					onClick={() => handleScroll('contact')}
				>
					{t('contact_us')}
				</span>
				<div className="flex gap-4">
					<a
						href="https://shake.arirang.co.id/member"
						className="flex justify-center items-center h-10 px-6 border border-blue-600 rounded-full"
					>
						<span className="font-normal text-lg text-blue-600">
							{t('login')}
						</span>
					</a>
					<a
						href="https://shake.arirang.co.id/member/register"
						className="flex justify-center items-center h-10 px-6 border bg-blue-600 border-blue-600 rounded-full"
					>
						<span className="font-normal text-lg text-white">
							{t('register')}
						</span>
					</a>
					<div ref={refDesktop} className="relative">
						<div
							className="relative bg-white border border-gray-100 p-2 rounded-full flex items-center gap-1 cursor-pointer"
							onClick={() => setShowLanguage(!showLanguage)}
						>
							<img
								className="h-6 shrink-0"
								src={
									currentLanguage === 'en'
										? require(`@/assets/icons/en.png`)
										: currentLanguage === 'kr'
											? require(`@/assets/icons/kr.png`)
											: require(`@/assets/icons/id.png`)
								}
								alt="arirang"
							/>
							<span className="font-semibold text-sm text-black">
								{currentLanguage?.toUpperCase()}
							</span>
							<img
								className="h-2 shrink-0"
								src={require('@/assets/icons/chevron.png')}
								alt="arirang"
							/>
						</div>
						<div
							className={cn(
								'absolute top-full right-0 invisible opacity-0 transition-all ease-in duration-300 translate-y-2',
								showLanguage && 'visible opacity-100 translate-y-0'
							)}
						>
							<div className="w-32 py-1 rounded-xl overflow-hidden bg-white border border-gray-100 mt-1">
								<div
									className="bg-white hover:bg-gray-50 flex-1 flex items-center gap-2 py-2 px-2 border-b border-b-gray-50 cursor-pointer"
									onClick={() => changeLanguage('id')}
								>
									<img
										className="h-6 shrink-0"
										src={require('@/assets/icons/id.png')}
										alt="arirang"
									/>
									<span className="font-semibold text-sm text-black">
										Indonesia
									</span>
								</div>
								<div
									className="bg-white hover:bg-gray-50 flex-1 flex items-center gap-2 py-2 px-2 border-b border-b-gray-50 cursor-pointer"
									onClick={() => changeLanguage('en')}
								>
									<img
										className="h-6 shrink-0"
										src={require('@/assets/icons/en.png')}
										alt="arirang"
									/>
									<span className="font-semibold text-sm text-black">
										English
									</span>
								</div>
								<div
									className="bg-white hover:bg-gray-50 flex-1 flex items-center gap-2 py-2 px-2 cursor-pointer"
									onClick={() => changeLanguage('kr')}
								>
									<img
										className="h-6 shrink-0"
										src={require('@/assets/icons/kr.png')}
										alt="arirang"
									/>
									<span className="font-semibold text-sm text-black">
										Korea
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</nav>
		</motion.header>
	)
}
